/**
 * Topbar.jsx - Top bar with view title, period selector, and refresh.
 *
 * Displays the current view's title and subtitle on the left, and
 * a period selector dropdown + refresh button on the right.
 *
 * Props:
 *   title       {string}    - Main heading (e.g. "Dashboard").
 *   subtitle    {string}    - Secondary text (e.g. "Vendor performance overview").
 *   period      {string}    - Current period key ("30d" | "90d" | "6m" | "12m" | "24m").
 *   onPeriod    {function}  - Called with the new period key when changed.
 *   onRefresh   {function}  - Called when the refresh button is clicked.
 *   refreshing  {boolean}   - When true, shows a spinning refresh icon.
 *   children    {node}      - Optional extra controls rendered between title and actions.
 *
 * Usage:
 *   <Topbar
 *     title="Dashboard"
 *     subtitle="Vendor performance overview"
 *     period={period}
 *     onPeriod={setPeriod}
 *     onRefresh={() => refetch()}
 *     refreshing={isFetching}
 *   />
 */

import { useState, useRef, useEffect } from "react";
import { RefreshCw, ChevronDown } from "lucide-react";

/* ------------------------------------------------------------------ */
/* Period options                                                      */
/* ------------------------------------------------------------------ */

const PERIODS = [
  { key: "30d", label: "Last 30 days" },
  { key: "90d", label: "Last 90 days" },
  { key: "6m", label: "Last 6 months" },
  { key: "12m", label: "Last 12 months" },
  { key: "24m", label: "Last 24 months" },
];

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

export default function Topbar({
  title,
  subtitle,
  period,
  onPeriod,
  onRefresh,
  refreshing,
  children,
}) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  /* Close dropdown on outside click */
  useEffect(() => {
    function handleClick(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const currentPeriod = PERIODS.find((p) => p.key === period) || PERIODS[0];

  return (
    <header style={styles.topbar}>
      {/* Left: Title & subtitle */}
      <div style={styles.left}>
        <h1 style={styles.title}>{title}</h1>
        {subtitle && <p style={styles.subtitle}>{subtitle}</p>}
      </div>

      {/* Center: Extra controls (optional) */}
      {children && <div style={styles.center}>{children}</div>}

      {/* Right: Period selector + Refresh */}
      <div style={styles.right}>
        {/* Period dropdown */}
        {onPeriod && (
          <div style={styles.dropdown} ref={dropdownRef}>
            <button
              style={styles.periodBtn}
              onClick={() => setDropdownOpen((o) => !o)}
              aria-haspopup="listbox"
              aria-expanded={dropdownOpen}
            >
              <span>{currentPeriod.label}</span>
              <ChevronDown size={14} style={{ opacity: 0.6 }} />
            </button>

            {dropdownOpen && (
              <ul style={styles.dropdownList} role="listbox">
                {PERIODS.map((p) => (
                  <li
                    key={p.key}
                    role="option"
                    aria-selected={p.key === period}
                    style={{
                      ...styles.dropdownItem,
                      ...(p.key === period ? styles.dropdownItemActive : {}),
                    }}
                    onClick={() => {
                      onPeriod(p.key);
                      setDropdownOpen(false);
                    }}
                  >
                    {p.label}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Refresh button */}
        {onRefresh && (
          <button
            className="btn"
            onClick={onRefresh}
            disabled={refreshing}
            aria-label="Refresh data"
            title="Refresh data"
          >
            <RefreshCw
              size={15}
              style={{
                animation: refreshing
                  ? "spin 0.8s linear infinite"
                  : "none",
              }}
            />
          </button>
        )}
      </div>
    </header>
  );
}

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const styles = {
  topbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    padding: "14px 24px",
    background: "var(--panel)",
    borderBottom: "1px solid var(--line)",
    flexWrap: "wrap",
    position: "sticky",
    top: 0,
    zIndex: 100,
  },
  left: {
    display: "flex",
    flexDirection: "column",
    gap: 1,
    minWidth: 0,
  },
  title: {
    fontSize: 18,
    fontWeight: 680,
    color: "var(--ink)",
    letterSpacing: "-0.01em",
    margin: 0,
  },
  subtitle: {
    fontSize: 12.5,
    color: "var(--ink-3)",
    margin: 0,
  },
  center: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flex: "0 0 auto",
  },
  right: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexShrink: 0,
  },
  dropdown: {
    position: "relative",
  },
  periodBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "7px 12px",
    fontSize: 13,
    fontWeight: 550,
    color: "var(--ink)",
    background: "var(--panel)",
    border: "1px solid var(--line)",
    borderRadius: "var(--radius-sm)",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  dropdownList: {
    position: "absolute",
    top: "calc(100% + 4px)",
    right: 0,
    zIndex: 200,
    listStyle: "none",
    margin: 0,
    padding: "4px 0",
    background: "var(--panel)",
    border: "1px solid var(--line)",
    borderRadius: "var(--radius-sm)",
    boxShadow: "var(--shadow-lg)",
    minWidth: 170,
  },
  dropdownItem: {
    padding: "7px 14px",
    fontSize: 13,
    color: "var(--ink-2)",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  dropdownItemActive: {
    background: "var(--accent-soft)",
    color: "var(--accent)",
    fontWeight: 650,
  },
};
